import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import NavigationControls from './components/NavigationControls';
import SlideIndicators from './components/SlideIndicators';
import TableOfContents from './components/TableOfContents';
import ProgressBar from './components/ProgressBar';
import ImageZoomModal from './components/ImageZoomModal';
import CompletionModal from './components/CompletionModal';
import PDFViewer from './components/pdfViewer'; // Import the PDF viewer component
import { CourseAPI } from 'service';



const InteractivePresentationViewer = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const courseId = params.get('courseId') || '1';
  const { name } = location.state || {};

  const [currentSlide, setCurrentSlide] = useState(1);
  const [isTableOfContentsOpen, setIsTableOfContentsOpen] = useState(false);
  const [isImageZoomOpen, setIsImageZoomOpen] = useState(false);
  const [zoomedImage, setZoomedImage] = useState({ src: '', alt: '' });
  const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
  const [startTime] = useState(Date.now());
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState('15 min');
  const [outline, setOutline] = useState([]);
  const [courseData, setCourseData] = useState(null);
  const [timeSpent, setTimeSpent] = useState(0); // session-based timer
  const [maxTimeSpent, setMaxTimeSpent] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeSpent(prev => {
        const updated = prev + 1;
        setMaxTimeSpent(max => Math.max(max, updated));
        return updated;
      });
    }, 60000); // 1 minute interval

    return () => clearInterval(interval);
  }, []);

  const [totalSlides, setTotalSlides] = useState(0);

  const fetchCurrentCourse = async () => {
    try {
      const response = await CourseAPI.getProgressById(courseId);
      if (response.success) {
        setCourseData(response.data.data || []);
      } else {
        console.error("Failed to fetch course outline:", response.error);
      }
    } catch (error) {
      console.error("Error fetching course outline:", error);
    }
  };

  const updateCurrentCourse = async (dontUpdate) => {
    try {
      const highestSlide = Math.max(courseData.currentSlide, currentSlide);
      const highestTime = Math.max(courseData.timeSpentInMinutes, maxTimeSpent);

      const incrementedSlide = currentSlide + 1;

      const progressPercentage = (incrementedSlide / totalSlides) * 100;

      const response = await CourseAPI.updateProgress(
        courseId,
        progressPercentage,
        highestTime,
        courseData.numberOfSlides,
        highestSlide >= totalSlides ? totalSlides : highestSlide + 1,
        courseData.title
      );

      if (response.success) {
        if (dontUpdate) return;
        setCourseData(prev => ({
          ...prev,
          ...response.data.data,
          currentSlide: highestSlide,
          timeSpentInMinutes: highestTime,
        }));
      } else {
        console.error("Failed to update course progress:", response.error);
      }
    } catch (error) {
      console.error("Error updating course progress:", error);
    }
  };

  useEffect(() => {
    fetchCurrentCourse();
  }, []);

  // Mock presentation data
  const presentationData = {
    chapterTitle: name || 'Introduction to AI',

  };

  // Calculate estimated time remaining
  useEffect(() => {
    const avgTimePerSlide = 90; // seconds
    const remainingSlides = totalSlides - currentSlide;
    const remainingSeconds = remainingSlides * avgTimePerSlide;
    const minutes = Math.ceil(remainingSeconds / 60);
    setEstimatedTimeRemaining(`${minutes} min`);
  }, [currentSlide, totalSlides]);

  // Auto-save progress
  useEffect(() => {
    localStorage.setItem('presentationProgress', JSON.stringify({
      chapterTitle: presentationData.chapterTitle,
      currentSlide,
      lastAccessed: Date.now()
    }));
  }, [currentSlide, presentationData.chapterTitle]);

  // Load saved progress
  useEffect(() => {
    if (courseData) {
      setCurrentSlide(courseData.currentSlide || 1);
    }
  }, [courseData]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft' && currentSlide > 1) {
        setCurrentSlide(prev => prev - 1);
      } else if (event.key === 'ArrowRight' && currentSlide < totalSlides) {
        setCurrentSlide(prev => prev + 1);
      } else if (event.key === 'Escape') {
        setIsTableOfContentsOpen(false);
        setIsImageZoomOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, totalSlides]);

  // Touch/swipe navigation
  useEffect(() => {
    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (event) => {
      touchStartX = event.changedTouches[0].screenX;
    };

    const handleTouchEnd = (event) => {
      touchEndX = event.changedTouches[0].screenX;
      handleSwipe();
    };

    const handleSwipe = () => {
      const swipeThreshold = 50;
      const swipeDistance = touchStartX - touchEndX;

      if (Math.abs(swipeDistance) > swipeThreshold) {
        if (swipeDistance > 0 && currentSlide < totalSlides) {
          // Swipe left - next slide
          setCurrentSlide(prev => prev + 1);
        } else if (swipeDistance < 0 && currentSlide > 1) {
          // Swipe right - previous slide
          setCurrentSlide(prev => prev - 1);
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [currentSlide, totalSlides]);

  // Check for completion
  useEffect(() => {
    if (currentSlide === totalSlides) {
      const timer = setTimeout(() => {
        setIsCompletionModalOpen(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentSlide, totalSlides]);

  const handlePrevious = () => {
    if (currentSlide > 1) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const handleNext = () => {
    const dontUpdate = true;
    if (currentSlide < totalSlides) {
      const nextSlide = currentSlide + 1;
      setCurrentSlide(nextSlide);
      updateCurrentCourse(dontUpdate);
    }
  };

  const handleSlideSelect = (slideNumber) => {
    setCurrentSlide(slideNumber);
  };

  const handleImageZoom = (imageSrc, imageAlt) => {
    setZoomedImage({ src: imageSrc, alt: imageAlt });
    setIsImageZoomOpen(true);
  };

  const handleToggleTableOfContents = () => {
    setIsTableOfContentsOpen(prev => !prev);
  };

  const getCompletionStats = () => {

    return {
      slidesViewed: currentSlide,
      timeSpent: `${Math.max(courseData?.timeSpentInMinutes, maxTimeSpent)} min`
    };
  };

  const [coursePdf, setCoursePdf] = useState("https://features-icons.s3.me-south-1.amazonaws.com/IntrotoAi1.0.pdf");

  useEffect(() => {
    if (courseId === '1') {
      setCoursePdf("https://features-icons.s3.me-south-1.amazonaws.com/IntrotoAi1.0.pdf");
    } else if (courseId === '2') {
      setCoursePdf("https://features-icons.s3.me-south-1.amazonaws.com/promptengineering.pdf");
    } else if (courseId === '3') {
      setCoursePdf("https://features-icons.s3.me-south-1.amazonaws.com/Course-Aiautomation.pdf");
    } else {
      setCoursePdf("https://features-icons.s3.me-south-1.amazonaws.com/IntrotoAi1.0.pdf");
    }
  }, [courseId]);


  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16">

        {/* Presentation Content */}
        <div className="relative">
          {/* Main Slide Area */}
          <div className="min-h-[calc(100vh-200px)] bg-surface">
            <PDFViewer key={coursePdf} setTotalSlides={setTotalSlides} setOutline={setOutline} currentSlide={currentSlide} file={coursePdf} />
          </div>
        </div>

        {/* Navigation Controls */}
        <NavigationControls
          currentSlide={currentSlide}
          totalSlides={totalSlides}
          onPrevious={handlePrevious}
          onNext={handleNext}
          canGoPrevious={currentSlide > 1}
          canGoNext={currentSlide < totalSlides}
        />

        {/* Table of Contents */}
        <TableOfContents
          slides={outline}
          currentSlide={currentSlide}
          isOpen={isTableOfContentsOpen}
          onToggle={handleToggleTableOfContents}
          onSlideSelect={handleSlideSelect}
        />

        {/* Slide Indicators */}
        <div className="border-t bg-surface border-border">
          <div className="max-w-6xl mx-auto">
            <SlideIndicators
              currentSlide={currentSlide}
              totalSlides={totalSlides}
              onSlideSelect={handleSlideSelect}
            />
          </div>
        </div>

        {/* Progress Bar */}
        <ProgressBar
          currentSlide={currentSlide}
          totalSlides={totalSlides}
          estimatedTimeRemaining={estimatedTimeRemaining}
        />
      </main>

      {/* Modals */}
      <ImageZoomModal
        isOpen={isImageZoomOpen}
        imageSrc={zoomedImage.src}
        imageAlt={zoomedImage.alt}
        onClose={() => setIsImageZoomOpen(false)}
      />

      <CompletionModal
        isOpen={isCompletionModalOpen}
        onClose={() => setIsCompletionModalOpen(false)}
        chapterTitle={presentationData.chapterTitle}
        completionStats={getCompletionStats()}
      />
    </div>
  );
};

export default InteractivePresentationViewer;